const _ = require("lodash");
const ErrorUtil = require("../../../../shared/ErrorUtil");
const {
	DEFAULT_PENCIL_LINE_WIDTH,
	DEFAULT_SOUNDS_VOLUME,
	DEFAULT_THEATER_VOLUME,
	DEFAULT_THEATER_PLAYBACK_RATE,
} = require("../../../../shared/Constants");
const GradeChapter = require("../../../../shared/GradeChapter");
const dataCharacters = require("../../../../shared/data/dbCharacters.json");

// Helpers for converting data sent from the backend into the format our vault
// understands.

const convertUser = function(userData) {
	const studentData = userData.studentData || {};
	const defaultSettings = {
		pencilLineWidth: DEFAULT_PENCIL_LINE_WIDTH,
		musicVolume: DEFAULT_SOUNDS_VOLUME,
		soundsVolume: DEFAULT_SOUNDS_VOLUME,
		musicEnabled: true,
		soundsEnabled: true,
		theaterCaptionsOn: false,
		theaterVolume: DEFAULT_THEATER_VOLUME,
		theaterSoundEnabled: true,
		theaterPlaybackRate: DEFAULT_THEATER_PLAYBACK_RATE,
	};

	const defaultMiscellaneousSettings = {
		suppressManualLockShowConfirm: false,
	};

	// If users HAVE ever seen (or SHOULD ever see) the tutorial, SOMETHING will be here,
	// and if so then we don't need to show the help blurb to them on the home page
	// but if there ISN'T any data in here, then they've never done the
	// tutorial and students SHOULD see this popup if they haven't viewed any pages yet
	const tutorialProgress = _.get(
		userData,
		[
			"studentData",
			"eventsTriggered",
			"tutorials",
			"studentTutorial",
			"currentPart",
		],
		null
	);
	const shouldShowHelp =
		studentData.viewedPages &&
		!studentData.viewedPages.length &&
		!tutorialProgress;
	return {
		//From userData
		userID: userData.id,
		platformUserID: userData.platformUserID,
		isInitialized: userData.isInitialized,
		globalUsername: userData.globalUsername,
		username: userData.username,
		schoolLogin: userData.schoolLogin,
		email: userData.email,
		phone: userData.phone,
		password: null,
		displayName: userData.displayName || userData.firstName || userData.email,
		firstName: userData.firstName,
		lastName: userData.lastName,
		domain: userData.domain,
		identifiesAsStudent: userData.identifiesAsStudent,
		identifiesAsParent: userData.identifiesAsParent,
		identifiesAsEducator: userData.identifiesAsEducator,
		ABSettings: userData.ABSettings || {},
		avatar: userData.avatarData,
		settings: _.extend(defaultSettings, userData.settingsData),
		miscellaneous: _.extend(defaultMiscellaneousSettings, userData.miscData),
		lastAcceptedPrivacyAt: userData.lastAcceptedPrivacyAt,
		//From studentData
		activity: _.extend({}, studentData.activity),
		bucks: studentData.bucks || 0,
		stars: studentData.stars || 0,
		trophies: studentData.trophies || 0,
		xp: studentData.xp || 0,
		currentStreak: studentData.currentStreak || 0,
		bestStreak: studentData.bestStreak || 0,
		gradeID: studentData.gradeID,
		placementChapterIndex: studentData.placementChapterIndex,
		totalTimeData: studentData.totalTimeData,
		lastPlayedData: studentData.lastPlayedData,
		mostRecentPageData: studentData.mostRecentPageData,
		lastLevelNotified: studentData.lastLevelNotified,
		viewedPages: studentData.viewedPages,
		sequenceData: studentData.sequenceData,
		eventsTriggered: {
			notifications: _.get(userData, "eventsTriggered.notifications", {}),
			tutorials: _.get(userData, "eventsTriggered.tutorials", {}),
		},
		createdAt: userData.createdAt,
		//This is the help blurb on the top bar
		showHelp: shouldShowHelp,
		//Do we need lastPlayed?
		defaultView: userData.defaultView,
		purchaseInfo: userData.purchaseInfo,
		schoolIDs: userData.schoolIDs,
		classURL: userData.classURL,
		staffForPurchaseID: userData.staffForPurchaseID,
		staffRoles: userData.staffRoles,
		staffClasses: userData.staffClassesTaught,
		staffSchools: userData.staffSchools,
		homework: studentData.homework,
		perms: userData.perms,
		roles: userData.roles,
		students: userData.students,
		hasTrainerPermission: userData.hasTrainerPermission,
		showHomework: studentData.showHomework,
		beastbuilderProgressUnlocked:
			_.get(userData, "beastbuilderLockObj.progressLock") === "unlocked",
		beastbuilderLockOverride: _.get(
			userData,
			"beastbuilderLockObj.lockOverride"
		),
		leaderboardsLockOverride: _.get(
			userData,
			"leaderboardsLockObj.lockOverride"
		),
		labProgressUnlocked:
			_.get(userData, "labLockObj.progressLock") === "unlocked",
		labLockOverride: _.get(userData, "labLockObj.lockOverride"),
		academyLeaderboardsLockOverride: _.get(
			userData,
			"academyLeaderboardsLockObj.lockOverride"
		),
		unacceptedTeacherInvites: userData.unacceptedTeacherInvites,
		oneOffRewards: userData.oneOffRewards,
		customScope: userData.customScope,
		isEmailVerified: !!userData.isEmailVerified,
		newEmail: userData.pendingEmail || null,
		platformEmailVerificationLoaded: !!userData.platformEmailVerificationLoaded,
	};
};

const convertChapter = function(chapterID, chapterData) {
	const gradeInfo = GradeChapter.chapterIDToContext(chapterID);
	if (!gradeInfo) {
		ErrorUtil.log(
			"E_CHAPTER_NO_GRADE",
			"Tried load chapter ID " + chapterID + " which had no grade"
		);
		return null;
	}

	return _.extend(
		{
			chapterID,
			gradeNumber: gradeInfo.gradeNumber,
			chapterIndex: gradeInfo.chapterIndex,
			displayName: chapterData.chapter.displayName,
			stars: chapterData.studentData.stars,
			blocksWithStars: chapterData.studentData.blocksWithStars,
			trophies: chapterData.studentData.trophies,
			totalTimeSpent: chapterData.studentData.timeSpent,
		},
		_.pick(chapterData, [
			"blockIDs",
			"testBlockID",
			"challengeBlockID",
			"reviewBlockID",
			"unlocked",
			"gradeUnlocked",
			"lockType",
			"gradeLockType",
			"hasUnlockedBlocks",
		])
	);
};

const convertBlock = function(blockID, chapterBlockIDs, chapterID, blockData) {
	let blockIndex = _.findIndex(chapterBlockIDs, x => x === blockID);
	if (blockIndex < 0) blockIndex = null;

	const gradeInfo = GradeChapter.chapterIDToContext(chapterID);
	const studentData = blockData.studentData;

	const beast = dataCharacters[blockData.characterID || 0];
	const blockStyleType =
		blockData.blockType === "character" ? beast : blockData.blockType;

	const trophyExists = blockData.hasTrophy;

	let unfinished = !!blockData.percentComplete;

	if (blockData.blockType === "test") {
		unfinished = !!blockData.numUnfinishedResults;
	}

	const convertedBlock = {
		blockID,
		chapterID,
		gradeNumber: gradeInfo.gradeNumber,
		chapterIndex: gradeInfo.chapterIndex,
		miscBlockName: null,
		blockType: blockData.blockType,
		blockStyleType,
		blockIndex,
		beast,
		displayName: blockData.displayName,
		unlocked: studentData.unlocked || false,
		lockOverride: studentData.lockOverride || null,
		progressLock: studentData.progressLock || "locked",
		progressLockSource: studentData.progressLockSource || null,
		ignoreCooldown: studentData.ignoreCooldown || false,
		restricted: false,
		restrictionData: studentData.interventionRestrictionData,
		underConstruction: blockData.underConstruction,
		instructions: false,
		middleInstructions: false,
		isGenerated: blockData.isGenerated || false,
		doesTrophyExist: trophyExists,
		isTrophyAvailable: trophyExists && studentData.trophyUnlocked,
		isTrophyComplete: studentData.trophyObtained || false,
		starsAvailable: blockData.maxStars,
		limitedStarsExpiration:
			blockData.maxStars < 3 ? blockData.maxStarExpiration : false,
		//If the block's result that would be resumed has limited stars
		limitedInProgressResult: blockData.limitedInProgressResult,
		resultLastUpdated: blockData.resultLastUpdated || false,
		starsObtained: studentData.stars || 0,
		unfinished, //TODO: Is this always correct?
		percentComplete: blockData.percentComplete,
		totalTimeSpent: blockData.totalTimeSpent,
		xp: blockData.xp,
		recentHistory: blockData.recentHistory,
		hasHistory: blockData.hasHistory,
		lastCompleted: blockData.lastCompleted,
		numSets: blockData.numSets,
		numResults: blockData.numResults,
		timesStarted: studentData.timesStarted || 0,
		timesCompleted: studentData.timesCompleted || 0,
		trophies: studentData.trophies,
		bookSectionID: blockData.sectionID,
		videoSetID: blockData.videoSetID,
		testCooldownReason: blockData.testCooldownReason,
		testCooldownExpiration: blockData.testCooldownExpiration,
		requirementLevel: blockData.requirementLevel,
		//TODO: Will we ever want timesStarted or timesIntervened?
	};

	return convertedBlock;
};

const convertChapterBlocks = function(chapterID, chapterData) {
	const auxStrings = ["review", "test"];
	if (chapterData.challengeBlockID) auxStrings.push("challenge");

	const blockIDs = chapterData.blockIDs;
	const blockData = _.map(blockIDs, bid =>
		_.find(chapterData.blocks, {id: bid})
	);
	const allBlockIDs = _.clone(blockIDs);
	const allBlockData = _.clone(blockData);
	_.forEach(auxStrings, s => {
		if (chapterData[s + "BlockID"]) {
			allBlockIDs.push(chapterData[s + "BlockID"]);
			allBlockData.push(chapterData[s + "Block"]);
		}
	});
	const blockModels = allBlockIDs.map((blockID, i) =>
		convertBlock(blockID, blockIDs, chapterID, allBlockData[i])
	);
	return _.zipObject(allBlockIDs, blockModels);
};

const convertLibraryChapters = function(chapterData) {
	return _.keyBy(chapterData, "chapterID");
};

const convertTheaterChapter = function(chapterData) {
	const blockData = chapterData.blocks;
	if (!blockData[0]) {
		return;
	}

	const chapterBlockIDs = blockData[0].trainerChapter.blockIDs;
	let convertedBlocks = _.map(blockData, block => {
		let blockIndex = _.findIndex(chapterBlockIDs, x => x === block.id);
		if (blockIndex < 0) blockIndex = null;
		return {
			chapterID: block.chapterID,
			blockID: block.id,
			displayName: block.displayName,
			videoSetID: block.videoSetID,
			model: block.studentVideoSet,
			firstVideoID: _.first(_.get(block, "videoSet.videoIDs")),
			firstVideoHash: _.get(block, "videoSet.firstVideoHash"),
			requirementLevel: block.requirementLevel,
			blockIndex,
		};
	});
	convertedBlocks = _.filter(
		convertedBlocks,
		block => !_.isNull(block.blockIndex)
	);
	// Sort by block index and re-index blocks. We do this because if the only blocks
	// in the chapter with videos have indices 1,4,17, we want their indices to be 0, 1, 2.
	convertedBlocks = _.map(
		_.sortBy(convertedBlocks, ["blockIndex"]),
		(block, i) => {
			block.blockIndex = i;
			return block;
		}
	);

	return convertedBlocks;
};

const convertTheaterSet = function(videoSetData) {
	const set = videoSetData.videoSet;
	return {
		videoSetID: set.id,
		model: {
			videoSetID: set.id,
			title: set.title,
			videoIDs: set.videoIDs,
			videoHashes: set.videoHashes,
			chapterID: set.chapterID,
			blockID: set.blockID,
		},
		videoModels: _.keyBy(set.studentVideo, v => v.videoID),
		blockModels: _.keyBy(set.blocks, b => b.id),
		labBlockModels: _.keyBy(set.labBlocks, lb => lb.id),
	};
};

const convertLabProblem = function(problem) {
	if (!problem) return null;
	const model = problem.contents;
	model.rendererID = problem.rendererID;
	return {
		problemID: problem.id,
		model,
	};
};

const convertLabType = function(typeID, labTypeData) {
	return {
		blurbProblem: convertLabProblem(labTypeData.blurbProblem),
		typeID,
		..._.pick(labTypeData, [
			"displayName",
			"order",
			"requiredChapterID",
			"requiredPhrase",
			"unlockLevel",
			"spaceUnlocked",
			"spaceLockType",
			"featuredBlocks",
			"blurbText",
			"blockIDs",
			"unlocked",
			"lockType",
		]),
	};
};

const convertLabTypeBlocks = function(typeID, labTypeData) {
	const blockIDs = labTypeData.blockIDs;
	const blockData = _.map(blockIDs, bid =>
		_.find(labTypeData.blocks, {id: bid})
	);

	const blockModels = blockIDs.map((blockID, i) =>
		convertLabBlock(blockID, typeID, blockData[i], i)
	);
	return _.zipObject(blockIDs, blockModels);
};

const convertLabBlock = function(blockID, typeID, blockData, i) {
	const studentData = blockData.studentData;

	const convertedBlock = {
		blockID,
		typeID,
		blockType: "lab",
		blockStyleType: "lab",
		celestialID: blockData.celestialID,
		celestialEarned: studentData.celestialEarned,
		displayName: blockData.displayName,
		unlocked: studentData.unlocked || false,
		lockOverride: studentData.lockOverride || null,
		progressLock: studentData.progressLock || "locked",
		underConstruction: blockData.underConstruction,
		releasedAt: blockData.releasedAt,
		erroredResult: blockData.erroredResult,
		instructions: false,
		middleInstructions: false,
		resultLastUpdated: blockData.resultLastUpdated || false,
		unfinished: !!blockData.percentComplete,
		percentComplete: blockData.percentComplete,
		totalTimeSpent: blockData.totalTimeSpent,
		xp: blockData.xp,
		timesCompleted: studentData.timesCompleted || 0,
		videoSetID: blockData.videoSetID,
		lessonImageProblem: convertLabProblem(blockData.lessonImageProblem),
	};

	return convertedBlock;
};

const convertMiscBlock = function(miscBlockName, blockData, extra) {
	const studentData = blockData.studentData;

	let unlocked = studentData.unlocked || false;
	let progressLock = studentData.progressLock || "locked";
	let beast;
	let blockStyleType;
	let gradeNumber;
	if (miscBlockName === "tutorial") {
		beast = "headmaster";
		blockStyleType = "test";
		unlocked = true;
		progressLock = "unlocked";
		gradeNumber = null;
	} else if (/^bookTest\d[A-D]$/.test(miscBlockName)) {
		beast = "headmaster";
		blockStyleType = "test";
		gradeNumber = parseInt(miscBlockName.substring(8, 9));
	}

	const unfinished = !!blockData.percentComplete;

	const convertedBlock = {
		blockID: blockData.id,
		chapterID: null,
		gradeNumber,
		chapterIndex: null,
		miscBlockName,
		blockType: blockData.blockType,
		blockStyleType,
		beast,
		displayName: blockData.displayName,
		unlocked,
		lockOverride: studentData.lockOverride || null,
		progressLock,
		ignoreCooldown: studentData.ignoreCooldown || false,
		restricted: false,
		restrictionData: studentData.interventionRestrictionData,
		underConstruction: blockData.underConstruction,
		instructions: false,
		middleInstructions: false,
		resultLastUpdated: blockData.resultLastUpdated || false,
		starsObtained: studentData.stars || 0,
		unfinished,
		percentComplete: blockData.percentComplete,
		totalTimeSpent: blockData.totalTimeSpent,
		xp: blockData.xp,
		recentHistory: blockData.recentHistory,
		hasHistory: blockData.hasHistory,
		numSets: blockData.numSets,
		numResults: blockData.numResults,
		timesCompleted: studentData.timesCompleted || 0,
		bookSectionID: blockData.sectionID,
		videoSetID: blockData.videoSetID,
		testCooldownReason: blockData.testCooldownReason,
		testCooldownExpiration: blockData.testCooldownExpiration,
	};

	if (extra) {
		_.extend(convertedBlock, extra);
	}

	return convertedBlock;
};

// rankingsData is an array of rows that represent a metric like XP or trophies earned.
// It's already sorted by that metric, so this function adds numerical rankings to the rows.
// For instance, if there are five rows and a two-way tie for second place, the rankings
// would be 1,2,2,4,5.
const convertLeaderboardsRankings = function(rankingsData) {
	let numTies = 1;
	let currentRank = 0;
	const rankingsArray = _.get(rankingsData, "rankings", []);
	const formattedRankings = _.map(rankingsArray, (row, index) => {
		if (row.rank) {
			// If the row already has a rank, we don't need to calculate it.
			return row;
		}

		if (index > 0 && row.metric === rankingsArray[index - 1].metric) {
			numTies++;
		} else {
			currentRank += numTies;
			numTies = 1;
		}
		return _.extend(row, {
			rank: currentRank,
		});
	});
	return {
		rows: formattedRankings,
	};
};

module.exports = {
	convertUser,
	convertChapter,
	convertBlock,
	convertChapterBlocks,
	convertLibraryChapters,
	convertTheaterChapter,
	convertTheaterSet,
	convertLabType,
	convertLabTypeBlocks,
	convertMiscBlock,
	convertLeaderboardsRankings,
};
